import { Select } from 'antd';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoCalendarOutline, IoGridOutline, IoMapOutline } from 'react-icons/io5';
import styled from 'styled-components';

import Logo from '@/atoms/logo';
import useTerminology from '@/common/hooks/useTerminology';
import { useSettingsStore } from '@core/settings/store/settings';

import { CityGroup, LandingViewMode } from './meeting-room-landing.types';

const StyledWhereSelect = styled.div`
  .ant-select-selector {
    border-radius: 24px !important;
    padding: 4px 16px !important;
    height: 48px !important;
    font-size: 16px !important;
  }
  .ant-select-selection-search-input {
    height: 40px !important;
  }
  .ant-select-selection-placeholder {
    line-height: 40px !important;
  }
  .ant-select-selection-item {
    line-height: 40px !important;
  }
  .ant-select-arrow {
    color: var(--main) !important;
  }
`;

interface MeetingRoomLandingHeaderProps {
  cityGroups: CityGroup[];
  selectedCity: string | null;
  onCityFilter: (city: string | null) => void;
  viewMode: LandingViewMode;
  onViewChange: (mode: LandingViewMode) => void;
}

const viewButtons: { mode: LandingViewMode; icon: typeof IoGridOutline; titleKey: string }[] = [
  { mode: 'grid', icon: IoGridOutline, titleKey: 'landing.header.gridView' },
  { mode: 'map', icon: IoMapOutline, titleKey: 'landing.header.mapView' },
  { mode: 'calendar', icon: IoCalendarOutline, titleKey: 'landing.header.calendarView' },
];

const MeetingRoomLandingHeader: FC<MeetingRoomLandingHeaderProps> = ({
  cityGroups,
  selectedCity,
  onCityFilter,
  viewMode,
  onViewChange,
}) => {
  const { t } = useTranslation();
  const { backgroundColor, textColor } = useSettingsStore();
  const { meetingRoomTerminology } = useTerminology();

  const cityOptions = useMemo(() => {
    return cityGroups.map((group) => ({
      label: group.city,
      value: group.city,
    }));
  }, [cityGroups]);

  return (
    <div
      className="flex flex-col items-center py-12 px-4 md:px-16"
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <Logo />
      <h1 className="text-3xl md:text-5xl font-bold mt-6 mb-2 m-0" style={{ color: textColor }}>
        {meetingRoomTerminology}
      </h1>
      <p className="text-base md:text-lg mb-8 m-0" style={{ color: textColor, opacity: 0.6 }}>
        {t('landing.header.subtitle')}
      </p>

      <div className="flex items-center gap-3 w-full max-w-lg">
        <StyledWhereSelect className="flex-1">
          <Select
            showSearch
            allowClear
            placeholder={t('landing.header.wherePlaceholder')}
            optionFilterProp="label"
            value={selectedCity}
            onChange={(value) => onCityFilter(value || null)}
            options={cityOptions}
            size="large"
            style={{ width: '100%' }}
          />
        </StyledWhereSelect>

        <div className="flex items-center gap-1 flex-shrink-0">
          {viewButtons.map(({ mode, icon: Icon, titleKey }) => (
            <button
              key={mode}
              onClick={() => onViewChange(mode)}
              className="flex items-center justify-center w-10 h-10 rounded-full border-none cursor-pointer transition-colors"
              style={{
                backgroundColor: viewMode === mode ? 'var(--main)' : '#F0F0F0',
                color: viewMode === mode ? '#FFFFFF' : '#707070',
              }}
              title={t(titleKey)}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MeetingRoomLandingHeader;
